// src/services/formService.js
import {
  collection,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { db, storage, auth } from '../config/firebase';

const FORMS_COLLECTION = 'docs-forms';
const FORM_BINDINGS_COLLECTION = 'docs-form-bindings';
const FORM_SUBMISSIONS_SUBCOLLECTION = 'submissions';

const normalizeEmails = (emails = []) => {
  if (!Array.isArray(emails)) return [];
  const cleaned = emails
    .map((email) => `${email || ''}`.trim().toLowerCase())
    .filter(Boolean);
  return Array.from(new Set(cleaned));
};

const sanitizeVisibility = (visibility) => {
  if (visibility === 'public' || visibility === 'restricted' || visibility === 'private') return visibility;
  return 'private';
};

const stripUndefinedDeep = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }
  if (value && typeof value === 'object') {
    // Keep non-plain objects (e.g. Firestore FieldValue sentinels) as-is.
    if (value.constructor !== Object) return value;
    const output = {};
    Object.entries(value).forEach(([key, item]) => {
      const cleaned = stripUndefinedDeep(item);
      if (cleaned !== undefined) output[key] = cleaned;
    });
    return output;
  }
  return value === undefined ? undefined : value;
};

const flattenSmartComponents = (items = []) =>
  items.flatMap((component) => (component?.type === 'componentGroup' ? flattenSmartComponents(component.children || []) : [component]));

const extractSmartChips = (smartDocument) => {
  const components = flattenSmartComponents(smartDocument?.components || []);
  return components
    .filter((component) => component?.props?.smartChipConfig?.enabled)
    .map((component) => ({
      componentId: component.id,
      variableName: component?.props?.variableName || component.id || '',
      smartChipConfig: component?.props?.smartChipConfig || {},
      smartChipData: component?.props?.smartChipData || {}
    }));
};

const sanitizeSmartChipSubmissions = (smartChips = []) => {
  if (!Array.isArray(smartChips)) return [];
  return smartChips
    .map((chip) => {
      const componentId = `${chip?.componentId || ''}`.trim();
      if (!componentId) return null;
      const variableName = `${chip?.variableName || ''}`.trim();
      const data = stripUndefinedDeep({
        text: `${chip?.data?.text || ''}`,
        rating: Number(chip?.data?.rating || 0),
        progress: Number(chip?.data?.progress || 0),
        status: ['red', 'orange', 'green'].includes(chip?.data?.status) ? chip.data.status : undefined
      });
      return stripUndefinedDeep({
        componentId,
        componentRef: stripUndefinedDeep({
          componentId: `${chip?.componentRef?.componentId || componentId}`.trim(),
          variableName: `${chip?.componentRef?.variableName || variableName}`.trim() || undefined
        }),
        variableName,
        type: `${chip?.type || ''}`.trim() || undefined,
        data
      });
    })
    .filter(Boolean);
};

export const formService = {
  async uploadPDF(file, userId) {
    if (!file) throw new Error('No file provided');
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to upload');

    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const fileRef = ref(storage, `forms/${ownerId}/${fileName}`);
    const snapshot = await uploadBytes(fileRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      url: downloadURL,
      path: snapshot.ref.fullPath,
      name: fileName,
      originalName: file.name,
      size: file.size,
      type: file.type
    };
  },

  async saveForm(formData, userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to save a form');
    const visibility = sanitizeVisibility(formData.visibility);
    const allowedEmails = visibility === 'restricted'
      ? normalizeEmails(formData.allowedEmails || [])
      : [];
    const formsRef = collection(db, FORMS_COLLECTION);
    const docRef = formData.documentId
      ? doc(db, FORMS_COLLECTION, formData.documentId)
      : doc(formsRef);
    const documentId = docRef.id;
    const payload = stripUndefinedDeep({
      ...formData,
      documentId,
      originalDocumentId: formData.originalDocumentId || documentId,
      userId: ownerId,
      visibility,
      allowedEmails,
      updatedAt: serverTimestamp()
    });

    if (formData.documentId) {
      // Existing doc edit path: avoid pre-read, update only.
      await updateDoc(docRef, payload);
    } else {
      // New doc create path.
      await setDoc(docRef, stripUndefinedDeep({
        ...payload,
        createdAt: serverTimestamp()
      }));
    }

    return {
      id: documentId,
      ...formData,
      documentId,
      originalDocumentId: formData.originalDocumentId || documentId,
      userId: ownerId,
      visibility,
      allowedEmails,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  },

  async saveFormBindings(bindingData, userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to save form bindings');

    const docRef = await addDoc(collection(db, FORM_BINDINGS_COLLECTION), {
      ...bindingData,
      userId: ownerId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return {
      id: docRef.id,
      ...bindingData,
      userId: ownerId,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  },

  async getUserForms(userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to load forms');

    const q = query(
      collection(db, FORMS_COLLECTION),
      where('userId', '==', ownerId),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(item => ({
      id: item.id,
      ...item.data()
    }));
  },

  async getFormById(formId) {
    const docRef = doc(db, FORMS_COLLECTION, formId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error('Form not found');
    }
    return { id: docSnap.id, ...docSnap.data() };
  },

  async updateForm(formId, updates) {
    const nextUpdates = { ...updates };
    if (Object.prototype.hasOwnProperty.call(nextUpdates, 'visibility')) {
      const visibility = sanitizeVisibility(nextUpdates.visibility);
      nextUpdates.visibility = visibility;
      if (visibility === 'restricted') {
        nextUpdates.allowedEmails = normalizeEmails(nextUpdates.allowedEmails || []);
      } else {
        nextUpdates.allowedEmails = [];
      }
    } else if (Object.prototype.hasOwnProperty.call(nextUpdates, 'allowedEmails')) {
      nextUpdates.allowedEmails = normalizeEmails(nextUpdates.allowedEmails || []);
    }

    const docRef = doc(db, FORMS_COLLECTION, formId);
    await updateDoc(docRef, stripUndefinedDeep({
      ...nextUpdates,
      updatedAt: serverTimestamp()
    }));
  },

  async publishForm(formId, publishData = {}, userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to publish a form');

    const visibility = sanitizeVisibility(publishData.visibility);
    const allowedEmails = visibility === 'restricted'
      ? normalizeEmails(publishData.allowedEmails || [])
      : [];
    const publishedRef = doc(collection(db, FORMS_COLLECTION, formId, 'published'));
    const smartDocument = publishData.smartDocument || null;
    const smartChips = publishData.smartChips || extractSmartChips(smartDocument);
    const files = publishData.files || (publishData.sourcePdf ? { sourcePdf: publishData.sourcePdf } : {});

    await setDoc(publishedRef, stripUndefinedDeep({
      formId,
      originalDocumentId: publishData.originalDocumentId || formId,
      visibility,
      allowedEmails,
      files,
      smartDocument,
      smartChips,
      userId: visibility === 'public' ? null : ownerId,
      publishedBy: ownerId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }));

    const formRef = doc(db, FORMS_COLLECTION, formId);
    await updateDoc(formRef, stripUndefinedDeep({
      visibility,
      allowedEmails,
      publishedAt: serverTimestamp(),
      latestPublishedId: publishedRef.id,
      updatedAt: serverTimestamp()
    }));

    return { id: publishedRef.id, formId, visibility, allowedEmails };
  },

  async deleteForm(formId, pdfStoragePath) {
    const docRef = doc(db, FORMS_COLLECTION, formId);
    await deleteDoc(docRef);

    if (pdfStoragePath) {
      const fileRef = ref(storage, pdfStoragePath);
      await deleteObject(fileRef);
    }
  },

  async cloneForm(formId, userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to clone a form');

    const sourceForm = await this.getFormById(formId);
    if (sourceForm.userId !== ownerId) {
      throw new Error('Only the form owner can clone this form');
    }

    const clonedTitle = `${sourceForm.title || 'Untitled Form'} (Copy)`;

    const clonedForm = await this.saveForm({
      title: clonedTitle,
      description: sourceForm.description || '',
      fields: Array.isArray(sourceForm.fields) ? sourceForm.fields : [],
      pages: Array.isArray(sourceForm.pages) ? sourceForm.pages : [],
      html: sourceForm.html || '',
      sections: Array.isArray(sourceForm.sections) ? sourceForm.sections : [],
      layoutMode: sourceForm.layoutMode || 'original',
      extraction: sourceForm.extraction || null,
      sourcePdf: sourceForm.sourcePdf || null,
      smartDocument: sourceForm.smartDocument || null,
      smartChips: Array.isArray(sourceForm.smartChips) ? sourceForm.smartChips : [],
      visibility: 'private',
      allowedEmails: []
    }, ownerId);

    return clonedForm;
  },

  async saveSubmission(formId, submissionData = {}, userId) {
    const formRef = doc(db, FORMS_COLLECTION, formId);
    const formSnap = await getDoc(formRef);
    if (!formSnap.exists()) {
      throw new Error('Form not found');
    }

    const authUser = auth.currentUser;
    const submitterId = userId || authUser?.uid || null;
    const submitterEmail = authUser?.email || null;
    const smartChips = sanitizeSmartChipSubmissions(submissionData.smartChips);

    const docRef = await addDoc(collection(db, FORMS_COLLECTION, formId, FORM_SUBMISSIONS_SUBCOLLECTION), stripUndefinedDeep({
      formId,
      formTitle: submissionData.formTitle || formSnap.data()?.title || '',
      values: submissionData.values || {},
      fields: Array.isArray(submissionData.fields) ? submissionData.fields : [],
      smartChips,
      mode: submissionData.mode || 'smart',
      submitComponentId: `${submissionData.submitComponentId || ''}`.trim() || null,
      submittedBy: submitterId,
      submitterEmail,
      submittedAtClient: submissionData.submittedAtClient || new Date().toISOString(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }));

    return { id: docRef.id, formId };
  },

  async getFormSubmissions(formId, maxResults = 25) {
    const safeLimit = Math.max(1, Math.min(100, Number(maxResults) || 25));
    const q = query(
      collection(db, FORMS_COLLECTION, formId, FORM_SUBMISSIONS_SUBCOLLECTION),
      orderBy('createdAt', 'desc'),
      limit(safeLimit)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => ({
      id: item.id,
      ...item.data()
    }));
  }
};
